import React, { useState, useEffect } from 'react';
import {
  Building2, Plus, Edit, Trash2, Copy, ChevronDown, ChevronRight,
  AlertCircle, CheckCircle, RefreshCw, Layers, GitBranch, Download,
  AlertTriangle, Merge, X, Search, Circle, Eye, EyeOff, Loader2,
} from 'lucide-react';
import apiClient from '../../config/api.config';
import DupliquerPourCSModal from './DupliquerPourCSModal';
import AutoCreerOptionsCSModal from './AutoCreerOptionsCSModal';
import { Card } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import { Badge } from '../../components/ui/Badge';
import { Spinner } from '../../components/ui/Spinner';
import {
  useGetPolesQuery,
  useCreatePoleMutation,
  useUpdatePoleMutation,
  useDeletePoleMutation,
  useGetParcoursListQuery,
  useCreateParcoursMutation,
  useUpdateParcoursMutation,
  useDeleteParcoursMutation,
  useGetOptionsQuery,
  useCreateOptionMutation,
  useUpdateOptionMutation,
  useDeleteOptionMutation,
  useGetClassesParNiveauQuery,
  useCreateClasseMutation,
  useUpdateClasseMutation,
  useDeleteClasseMutation,
  useCopierNiveauMutation,
  useCheckDoublonsQuery,
  useSyncFromEtudiantsMutation,
  useMergeOptionsMutation,
  useMergeParcoursMutation,
  usePurgerStructuresMutation,
  PoleList,
  ParcoursList,
  OptionList,
  PoleCreate,
  ParcoursCreate,
  OptionCreate,
  ClasseCreate,
  ClasseList,
  DoublonsWarning,
  DoublonGroup,
} from '../../services/polesOptionsApi';

// Niveaux académiques
const NIVEAUX = [
  { value: 'L1', label: 'Licence 1' },
  { value: 'L2', label: 'Licence 2' },
  { value: 'L3', label: 'Licence 3' },
  { value: 'M1', label: 'Master 1' },
  { value: 'M2', label: 'Master 2' },
];

export const GestionOptions: React.FC = () => {
  // États pour les onglets
  const [activeTab, setActiveTab] = useState<'poles' | 'parcours' | 'options' | 'classes'>('options');

  // États pour les modales
  const [showPoleForm, setShowPoleForm] = useState(false);
  const [showParcoursForm, setShowParcoursForm] = useState(false);
  const [showOptionForm, setShowOptionForm] = useState(false);
  const [showClasseForm, setShowClasseForm] = useState(false);
  const [showCopyForm, setShowCopyForm] = useState(false);

  // États pour les éditions
  const [editingPole, setEditingPole] = useState<PoleList | null>(null);
  const [editingParcours, setEditingParcours] = useState<ParcoursList | null>(null);
  const [editingOption, setEditingOption] = useState<OptionList | null>(null);
  const [editingClasse, setEditingClasse] = useState<ClasseList | null>(null);
  const [classeCode, setClasseCode] = useState('');

  // États pour les formulaires
  const [poleForm, setPoleForm] = useState<PoleCreate>({ code: '', nom: '', description: '' });
  const [parcoursForm, setParcoursForm] = useState<ParcoursCreate>({ code: '', nom: '', pole: 0, description: '' });
  const [optionForm, setOptionForm] = useState<OptionCreate>({ code: '', nom: '', pole: 0, parcours: null });
  const [classeForm, setClasseForm] = useState<ClasseCreate>({ niveau: '', option: 0 });
  const [copyForm, setCopyForm] = useState({ niveau_source: '', niveau_destination: '' });

  // États pour l'affichage
  const [expandedPoles, setExpandedPoles] = useState<number[]>([]);
  const [expandedParcours, setExpandedParcours] = useState<number[]>([]);
  const [expandedNiveaux, setExpandedNiveaux] = useState<string[]>([]);

  // Recherche + filtres par onglet
  const [searchPoles, setSearchPoles] = useState('');
  const [searchParcours, setSearchParcours] = useState('');
  const [filterParcoursPole, setFilterParcoursPole] = useState<number | ''>('');
  const [searchOptions, setSearchOptions] = useState('');
  const [filterOptionsPole, setFilterOptionsPole] = useState<number | ''>('');
  const [filterOptionsParcours, setFilterOptionsParcours] = useState<number | ''>('');
  const [searchClasses, setSearchClasses] = useState('');
  const [filterClassesNiveau, setFilterClassesNiveau] = useState<string>('');
  const [filterClassesPole, setFilterClassesPole] = useState<number | ''>('');

  // Notifications
  const [notification, setNotification] = useState<{ type: 'success' | 'error'; message: string } | null>(null);

  // Confirmation de suppression
  const [deleteConfirm, setDeleteConfirm] = useState<{
    type: 'pole' | 'parcours' | 'option' | 'classe';
    id: number;
    label: string;
    warning: string;
  } | null>(null);

  // API Queries
  const { data: poles, isLoading: isLoadingPoles, refetch: refetchPoles } = useGetPolesQuery();
  const { data: parcoursList, isLoading: isLoadingParcours, refetch: refetchParcours } = useGetParcoursListQuery();
  const { data: options, isLoading: isLoadingOptions, refetch: refetchOptions } = useGetOptionsQuery();
  const { data: classesParNiveau, isLoading: isLoadingClasses, refetch: refetchClasses } = useGetClassesParNiveauQuery();

  // Mutations
  const [createPole, { isLoading: isCreatingPole }] = useCreatePoleMutation();
  const [updatePole, { isLoading: isUpdatingPole }] = useUpdatePoleMutation();
  const [deletePole] = useDeletePoleMutation();
  const [createParcours, { isLoading: isCreatingParcours }] = useCreateParcoursMutation();
  const [updateParcours, { isLoading: isUpdatingParcours }] = useUpdateParcoursMutation();
  const [deleteParcours] = useDeleteParcoursMutation();
  const [createOption, { isLoading: isCreatingOption }] = useCreateOptionMutation();
  const [updateOption, { isLoading: isUpdatingOption }] = useUpdateOptionMutation();
  const [deleteOption] = useDeleteOptionMutation();
  const [createClasse, { isLoading: isCreatingClasse }] = useCreateClasseMutation();
  const [updateClasse, { isLoading: isUpdatingClasse }] = useUpdateClasseMutation();
  const [deleteClasse] = useDeleteClasseMutation();
  const [copierNiveau, { isLoading: isCopying }] = useCopierNiveauMutation();
  const [purgerStructures, { isLoading: isPurging }] = usePurgerStructuresMutation();

  // Purge structurelle
  const [showPurgeModal, setShowPurgeModal] = useState(false);
  const [purgeConfirmText, setPurgeConfirmText] = useState('');
  const PURGE_CONFIRMATION = 'PURGER_STRUCTURES';

  const handlePurgeStructures = async () => {
    try {
      const res = await purgerStructures({ confirmation: PURGE_CONFIRMATION }).unwrap();
      setNotification({
        type: 'success',
        message: res.message || 'Structure supprimée avec succès',
      });
      setShowPurgeModal(false);
      setPurgeConfirmText('');
      refetchPoles(); refetchParcours(); refetchOptions(); refetchClasses();
    } catch (e: any) {
      setNotification({
        type: 'error',
        message: e?.data?.error || 'Erreur lors de la purge',
      });
    }
  };
  const [syncFromEtudiants, { isLoading: isSyncing }] = useSyncFromEtudiantsMutation();
  const [mergeOptions, { isLoading: isMergingOptions }] = useMergeOptionsMutation();
  const [mergeParcours, { isLoading: isMergingParcours }] = useMergeParcoursMutation();

  // Vérification automatique des doublons au chargement
  const { data: doublonsData, refetch: refetchDoublons } = useCheckDoublonsQuery();

  // État pour les doublons détectés
  const [doublons, setDoublons] = useState<DoublonsWarning | null>(null);
  const [doublonsDismissed, setDoublonsDismissed] = useState(false);

  // Charger les doublons depuis la query au montage
  useEffect(() => {
    if (doublonsData && !doublonsDismissed) {
      if (doublonsData.options.length > 0 || doublonsData.parcours.length > 0) {
        setDoublons(doublonsData);
      }
    }
  }, [doublonsData, doublonsDismissed]);
  const [mergeConfirm, setMergeConfirm] = useState<{
    type: 'option' | 'parcours';
    group: DoublonGroup;
  } | null>(null);

  const handleSync = async () => {
    try {
      const result = await syncFromEtudiants().unwrap();
      showNotification('success', result.message);
      // Stocker les doublons détectés
      setDoublonsDismissed(false);
      if (result.doublons &&
          (result.doublons.options.length > 0 || result.doublons.parcours.length > 0)) {
        setDoublons(result.doublons);
      } else {
        setDoublons(null);
      }
    } catch (error: any) {
      showNotification('error', error?.data?.error || 'Erreur lors de la synchronisation');
    }
  };

  const handleMerge = async (type: 'option' | 'parcours', group: DoublonGroup) => {
    try {
      const merge_ids = group.duplicates.map(d => d.id);
      if (type === 'option') {
        await mergeOptions({ canonicalId: group.canonical_id, merge_ids }).unwrap();
      } else {
        await mergeParcours({ canonicalId: group.canonical_id, merge_ids }).unwrap();
      }
      showNotification('success', `Fusion réussie vers "${group.canonical}"`);
      // Retirer le groupe fusionné de la liste
      if (doublons) {
        const updated = { ...doublons };
        if (type === 'option') {
          updated.options = updated.options.filter(g => g.canonical_id !== group.canonical_id);
        } else {
          updated.parcours = updated.parcours.filter(g => g.canonical_id !== group.canonical_id);
        }
        if (updated.options.length === 0 && updated.parcours.length === 0) {
          setDoublons(null);
        } else {
          setDoublons(updated);
        }
      }
      setMergeConfirm(null);
    } catch (error: any) {
      showNotification('error', error?.data?.error || 'Erreur lors de la fusion');
      setMergeConfirm(null);
    }
  };

  const showNotification = (type: 'success' | 'error', message: string) => {
    setNotification({ type, message });
    setTimeout(() => setNotification(null), 5000);
  };

  // ============ Pôles ============
  const handleSavePole = async () => {
    try {
      if (editingPole) {
        await updatePole({ id: editingPole.id, data: poleForm }).unwrap();
        showNotification('success', 'Pôle modifié avec succès');
      } else {
        await createPole(poleForm).unwrap();
        showNotification('success', 'Pôle créé avec succès');
      }
      setShowPoleForm(false);
      resetPoleForm();
    } catch (err: any) {
      showNotification('error', err.data?.detail || 'Erreur lors de l\'enregistrement');
    }
  };

  const askDeletePole = (pole: PoleList) => {
    setDeleteConfirm({
      type: 'pole',
      id: pole.id,
      label: `${pole.nom} (${pole.code})`,
      warning: 'Les parcours, options et classes associés seront également supprimés.',
    });
  };

  const handleDeletePole = async (id: number) => {
    try {
      await deletePole(id).unwrap();
      showNotification('success', 'Pôle supprimé');
    } catch (err: any) {
      showNotification('error', err.data?.detail || 'Erreur lors de la suppression');
    }
  };

  const openEditPole = (pole: PoleList) => {
    setEditingPole(pole);
    setPoleForm({ code: pole.code, nom: pole.nom, description: pole.description || '' });
    setShowPoleForm(true);
  };

  const resetPoleForm = () => {
    setEditingPole(null);
    setPoleForm({ code: '', nom: '', description: '' });
  };

  // ============ Parcours ============
  const handleSaveParcours = async () => {
    try {
      if (editingParcours) {
        await updateParcours({ id: editingParcours.id, data: parcoursForm }).unwrap();
        showNotification('success', 'Parcours modifié avec succès');
      } else {
        await createParcours(parcoursForm).unwrap();
        showNotification('success', 'Parcours créé avec succès');
      }
      setShowParcoursForm(false);
      resetParcoursForm();
    } catch (err: any) {
      showNotification('error', err.data?.detail || 'Erreur lors de l\'enregistrement');
    }
  };

  const askDeleteParcours = (p: ParcoursList) => {
    setDeleteConfirm({
      type: 'parcours',
      id: p.id,
      label: `${p.nom} (${p.code})`,
      warning: 'Les options associées seront détachées (non supprimées).',
    });
  };

  const handleDeleteParcours = async (id: number) => {
    try {
      await deleteParcours(id).unwrap();
      showNotification('success', 'Parcours supprimé');
    } catch (err: any) {
      showNotification('error', err.data?.detail || 'Erreur lors de la suppression');
    }
  };

  const openEditParcours = (p: ParcoursList) => {
    setEditingParcours(p);
    setParcoursForm({ code: p.code, nom: p.nom, pole: p.pole, description: p.description || '' });
    setShowParcoursForm(true);
  };

  const resetParcoursForm = () => {
    setEditingParcours(null);
    setParcoursForm({ code: '', nom: '', pole: poles?.[0]?.id || 0, description: '' });
  };

  // ============ Options ============
  const handleSaveOption = async () => {
    try {
      if (editingOption) {
        await updateOption({ id: editingOption.id, data: optionForm }).unwrap();
        showNotification('success', 'Option modifiée avec succès');
      } else {
        await createOption(optionForm).unwrap();
        showNotification('success', 'Option créée avec succès');
      }
      setShowOptionForm(false);
      resetOptionForm();
    } catch (err: any) {
      showNotification('error', err.data?.detail || 'Erreur lors de l\'enregistrement');
    }
  };

  const askDeleteOption = (option: OptionList) => {
    setDeleteConfirm({
      type: 'option',
      id: option.id,
      label: `${option.nom} (${option.code})`,
      warning: 'Les classes et UE/matières associées à cette option seront automatiquement supprimées. Cette action est irréversible.',
    });
  };

  const handleDeleteOption = async (id: number) => {
    try {
      await deleteOption(id).unwrap();
      showNotification('success', 'Option supprimée (classes associées supprimées)');
      refetchDoublons();
    } catch (err: any) {
      showNotification('error', err.data?.detail || 'Erreur lors de la suppression');
    }
  };

  const [duplicatingCsId, setDuplicatingCsId] = useState<number | null>(null);
  const [optionCsModal, setOptionCsModal] = useState<OptionList | null>(null);
  const [showAutoCSModal, setShowAutoCSModal] = useState(false);

  const handleDupliquerCS = (option: OptionList) => {
    setOptionCsModal(option);
  };

  const openEditOption = (option: OptionList) => {
    setEditingOption(option);
    setOptionForm({
      code: option.code,
      nom: option.nom,
      pole: option.pole,
      parcours: option.parcours ?? null,
      description: option.description || '',
    });
    setShowOptionForm(true);
  };

  const resetOptionForm = () => {
    setEditingOption(null);
    setOptionForm({ code: '', nom: '', pole: poles?.[0]?.id || 0, parcours: null });
  };

  // ============ Classes ============
  const handleSaveClasse = async () => {
    try {
      if (editingClasse) {
        const data: any = { ...classeForm };
        if (classeCode && classeCode !== editingClasse.code) {
          data.code = classeCode;
        }
        await updateClasse({ id: editingClasse.id, data }).unwrap();
        showNotification('success', 'Classe modifiée avec succès');
      } else {
        await createClasse(classeForm).unwrap();
        showNotification('success', 'Classe créée avec succès');
      }
      setShowClasseForm(false);
      resetClasseForm();
    } catch (err: any) {
      showNotification('error', err.data?.detail || err.data?.code?.[0] || 'Erreur lors de l\'enregistrement');
    }
  };

  const resetClasseForm = () => {
    setEditingClasse(null);
    setClasseForm({ niveau: '', option: 0 });
    setClasseCode('');
  };

  const startEditClasse = (classe: ClasseList) => {
    setEditingClasse(classe);
    setClasseForm({ niveau: classe.niveau, option: classe.option_id });
    setClasseCode(classe.code);
    setShowClasseForm(true);
  };

  const askDeleteClasse = (classe: { id: number; code: string; option_nom: string }) => {
    setDeleteConfirm({
      type: 'classe',
      id: classe.id,
      label: `${classe.code} (${classe.option_nom})`,
      warning: 'Les étudiants et notes associés à cette classe seront impactés.',
    });
  };

  const handleDeleteClasse = async (id: number) => {
    try {
      await deleteClasse(id).unwrap();
      showNotification('success', 'Classe supprimée');
    } catch (err: any) {
      showNotification('error', err.data?.detail || 'Erreur lors de la suppression');
    }
  };

  const handleConfirmDelete = async () => {
    if (!deleteConfirm) return;
    const { type, id } = deleteConfirm;
    setDeleteConfirm(null);
    switch (type) {
      case 'pole': return handleDeletePole(id);
      case 'parcours': return handleDeleteParcours(id);
      case 'option': return handleDeleteOption(id);
      case 'classe': return handleDeleteClasse(id);
    }
  };

  const handleCopierNiveau = async () => {
    if (!copyForm.niveau_source || !copyForm.niveau_destination) return;
    try {
      const result = await copierNiveau(copyForm).unwrap();
      showNotification('success', result.message);
      setShowCopyForm(false);
      setCopyForm({ niveau_source: '', niveau_destination: '' });
    } catch (err: any) {
      showNotification('error', err.data?.detail || 'Erreur lors de la copie');
    }
  };

  // Toggle pour l'affichage
  const togglePole = (poleId: number) => {
    setExpandedPoles(prev =>
      prev.includes(poleId) ? prev.filter(id => id !== poleId) : [...prev, poleId]
    );
  };

  const toggleParcoursItem = (parcoursId: number) => {
    setExpandedParcours(prev =>
      prev.includes(parcoursId) ? prev.filter(id => id !== parcoursId) : [...prev, parcoursId]
    );
  };

  const toggleNiveau = (niveau: string) => {
    setExpandedNiveaux(prev =>
      prev.includes(niveau) ? prev.filter(n => n !== niveau) : [...prev, niveau]
    );
  };

  const getOptionsByPole = (poleId: number) => options?.filter(o => o.pole === poleId) || [];
  const getParcoursByPole = (poleId: number) => parcoursList?.filter(p => p.pole === poleId) || [];
  const getOptionsByParcours = (parcoursId: number) => options?.filter(o => o.parcours === parcoursId) || [];
  const getParcoursForPole = (poleId: number) => parcoursList?.filter(p => p.pole === poleId) || [];

  if (isLoadingPoles || isLoadingOptions) {
    return (
      <div className="flex items-center justify-center h-64">
        <Spinner size="lg" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Notification */}
      {notification && (
        <div className={`fixed top-4 right-4 z-50 p-4 rounded-lg shadow-lg ${
          notification.type === 'success'
            ? 'bg-green-100 border-green-500 text-green-800 dark:bg-green-900/40 dark:border-green-600 dark:text-green-300'
            : 'bg-red-100 border-red-500 text-red-800 dark:bg-red-900/40 dark:border-red-600 dark:text-red-300'
        } border`}>
          <div className="flex items-center">
            {notification.type === 'success' ? <CheckCircle size={20} className="mr-2" /> : <AlertCircle size={20} className="mr-2" />}
            {notification.message}
          </div>
        </div>
      )}

      {/* Alertes doublons */}
      {doublons && (
        <Card className="border-amber-400 dark:border-amber-600 bg-amber-50 dark:bg-amber-900/20">
          <div className="p-4">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2 text-amber-800 dark:text-amber-300">
                <AlertTriangle size={20} />
                <h3 className="font-semibold">
                  Doublons potentiels détectés ({doublons.options.length + doublons.parcours.length} groupe(s))
                </h3>
              </div>
              <button
                onClick={() => { setDoublons(null); setDoublonsDismissed(true); }}
                className="text-amber-600 hover:text-amber-800 dark:text-amber-400 dark:hover:text-amber-200"
              >
                <X size={18} />
              </button>
            </div>

            {/* Doublons Options */}
            {doublons.options.length > 0 && (
              <div className="mb-3">
                <h4 className="text-sm font-medium text-amber-700 dark:text-amber-400 mb-2">Options :</h4>
                <div className="space-y-2">
                  {doublons.options.map((group) => (
                    <div key={group.canonical_id} className="flex items-center justify-between bg-white dark:bg-gray-800 rounded-lg p-3 border border-amber-200 dark:border-amber-700">
                      <div className="flex-1">
                        <span className="font-medium text-gray-900 dark:text-gray-100">{group.canonical}</span>
                        <span className="text-xs text-gray-500 dark:text-gray-400 ml-2">({group.nb_etudiants} étudiants)</span>
                        <div className="flex flex-wrap gap-2 mt-1">
                          {group.duplicates.map((dup) => (
                            <span key={dup.id} className="inline-flex items-center gap-1 text-sm">
                              <span className="text-amber-700 dark:text-amber-300">≈</span>
                              <span className="text-gray-700 dark:text-gray-300">{dup.name}</span>
                              <Badge variant="warning" className="text-xs">{Math.round(dup.similarity * 100)}%</Badge>
                              <span className="text-xs text-gray-500">({dup.nb_etudiants} ét.)</span>
                            </span>
                          ))}
                        </div>
                      </div>
                      <Button
                        variant="outline"
                        size="sm"
                        icon={<Merge size={14} />}
                        onClick={() => setMergeConfirm({ type: 'option', group })}
                        disabled={isMergingOptions}
                        className="ml-3 shrink-0"
                      >
                        Fusionner
                      </Button>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Doublons Parcours */}
            {doublons.parcours.length > 0 && (
              <div>
                <h4 className="text-sm font-medium text-amber-700 dark:text-amber-400 mb-2">Parcours :</h4>
                <div className="space-y-2">
                  {doublons.parcours.map((group) => (
                    <div key={group.canonical_id} className="flex items-center justify-between bg-white dark:bg-gray-800 rounded-lg p-3 border border-amber-200 dark:border-amber-700">
                      <div className="flex-1">
                        <span className="font-medium text-gray-900 dark:text-gray-100">{group.canonical}</span>
                        <span className="text-xs text-gray-500 dark:text-gray-400 ml-2">({group.nb_etudiants} étudiants)</span>
                        <div className="flex flex-wrap gap-2 mt-1">
                          {group.duplicates.map((dup) => (
                            <span key={dup.id} className="inline-flex items-center gap-1 text-sm">
                              <span className="text-amber-700 dark:text-amber-300">≈</span>
                              <span className="text-gray-700 dark:text-gray-300">{dup.name}</span>
                              <Badge variant="warning" className="text-xs">{Math.round(dup.similarity * 100)}%</Badge>
                              <span className="text-xs text-gray-500">({dup.nb_etudiants} ét.)</span>
                            </span>
                          ))}
                        </div>
                      </div>
                      <Button
                        variant="outline"
                        size="sm"
                        icon={<Merge size={14} />}
                        onClick={() => setMergeConfirm({ type: 'parcours', group })}
                        disabled={isMergingParcours}
                        className="ml-3 shrink-0"
                      >
                        Fusionner
                      </Button>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </Card>
      )}

      {/* Modal confirmation fusion */}
      {mergeConfirm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <Card className="w-full max-w-md mx-4">
            <div className="p-6">
              <div className="flex items-center gap-2 text-amber-600 dark:text-amber-400 mb-4">
                <AlertTriangle size={24} />
                <h3 className="text-lg font-semibold">Confirmer la fusion</h3>
              </div>
              <p className="text-gray-700 dark:text-gray-300 mb-2">
                Les {mergeConfirm.type === 'option' ? 'options' : 'parcours'} suivant(e)s seront fusionné(e)s :
              </p>
              <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-3 mb-4">
                <div className="space-y-1">
                  {mergeConfirm.group.duplicates.map((dup) => (
                    <div key={dup.id} className="flex items-center text-sm">
                      <Trash2 size={14} className="text-red-500 mr-2 shrink-0" />
                      <span className="text-gray-600 dark:text-gray-400 line-through">{dup.name}</span>
                      <span className="text-xs text-gray-500 ml-2">({dup.nb_etudiants} ét.)</span>
                    </div>
                  ))}
                  <div className="flex items-center text-sm mt-2 pt-2 border-t border-gray-200 dark:border-gray-700">
                    <CheckCircle size={14} className="text-green-500 mr-2 shrink-0" />
                    <span className="font-medium text-gray-900 dark:text-gray-100">→ {mergeConfirm.group.canonical}</span>
                    <span className="text-xs text-gray-500 ml-2">({mergeConfirm.group.nb_etudiants} ét.)</span>
                  </div>
                </div>
              </div>
              <p className="text-xs text-gray-500 dark:text-gray-400 mb-4">
                Les étudiants et UE associés seront automatiquement réassignés.
              </p>
              <div className="flex justify-end gap-2">
                <Button variant="outline" onClick={() => setMergeConfirm(null)}>
                  Annuler
                </Button>
                <Button
                  variant="primary"
                  icon={<Merge size={16} />}
                  onClick={() => handleMerge(mergeConfirm.type, mergeConfirm.group)}
                  disabled={isMergingOptions || isMergingParcours}
                >
                  {(isMergingOptions || isMergingParcours) ? 'Fusion...' : 'Confirmer la fusion'}
                </Button>
              </div>
            </div>
          </Card>
        </div>
      )}

      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-gray-100">Gestion des Options et Classes</h1>
          <p className="text-gray-600 dark:text-gray-400 mt-2">Configurez les pôles, parcours, options et classes de votre établissement</p>
        </div>
        <div className="flex gap-2">
          <Button
            variant="primary"
            icon={isSyncing ? <RefreshCw size={16} className="animate-spin" /> : <Download size={16} />}
            onClick={handleSync}
            disabled={isSyncing}
          >
            {isSyncing ? 'Synchronisation...' : 'Importer depuis étudiants'}
          </Button>
          <Button
            variant="outline"
            icon={<RefreshCw size={16} />}
            onClick={() => {
              refetchPoles();
              refetchParcours();
              refetchOptions();
              refetchClasses();
              // Relancer la détection de doublons et réafficher les alertes
              setDoublonsDismissed(false);
              refetchDoublons();
            }}
          >
            Actualiser
          </Button>
          <Button
            variant="danger"
            icon={<Trash2 size={16} />}
            onClick={() => { setShowPurgeModal(true); setPurgeConfirmText(''); }}
            title="Supprimer TOUS les pôles, parcours, options et classes"
          >
            Purger structure
          </Button>
        </div>
      </div>

      {/* Modal de confirmation purge structurelle */}
      {showPurgeModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-2xl w-full max-w-md border border-red-300 dark:border-red-700">
            <div className="p-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="p-2 bg-red-100 dark:bg-red-900/30 rounded-full">
                  <AlertTriangle size={24} className="text-red-600 dark:text-red-400" />
                </div>
                <h3 className="text-lg font-bold text-gray-900 dark:text-gray-100">Purger toute la structure</h3>
              </div>
              <div className="space-y-3 text-sm text-gray-600 dark:text-gray-400">
                <p>Cette action supprime <strong className="text-red-600 dark:text-red-400">l'intégralité</strong> de la structure organisationnelle :</p>
                <ul className="list-disc list-inside space-y-1 ml-2">
                  <li>{classesParNiveau?.reduce((s, n) => s + n.total, 0) || 0} classe(s)</li>
                  <li>{options?.length || 0} option(s)</li>
                  <li>{parcoursList?.length || 0} parcours</li>
                  <li>{poles?.length || 0} pôle(s)</li>
                </ul>
                <p className="text-amber-700 dark:text-amber-400 bg-amber-50 dark:bg-amber-900/20 p-2 rounded text-xs">
                  <strong>Note :</strong> Les UE, matières, étudiants et notes ne sont pas touchés. Cependant les étudiants existants perdront la référence à leurs pôle/option/classe.
                </p>
              </div>
              <div className="mt-4">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Tapez <code className="px-1.5 py-0.5 bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300 rounded text-xs font-mono">{PURGE_CONFIRMATION}</code> pour confirmer
                </label>
                <input
                  type="text"
                  value={purgeConfirmText}
                  onChange={(e) => setPurgeConfirmText(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 font-mono text-sm focus:ring-2 focus:ring-red-500 focus:outline-none"
                  placeholder={PURGE_CONFIRMATION}
                />
              </div>
              <div className="flex justify-end gap-3 mt-6">
                <Button
                  variant="outline"
                  onClick={() => { setShowPurgeModal(false); setPurgeConfirmText(''); }}
                  disabled={isPurging}
                >
                  Annuler
                </Button>
                <Button
                  variant="danger"
                  onClick={handlePurgeStructures}
                  disabled={purgeConfirmText !== PURGE_CONFIRMATION || isPurging}
                  icon={isPurging ? <RefreshCw size={16} className="animate-spin" /> : <Trash2 size={16} />}
                >
                  {isPurging ? 'Suppression...' : 'Confirmer la purge'}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Tabs */}
      <div className="border-b border-gray-200 dark:border-gray-700">
        <nav className="flex space-x-8">
          {[
            { id: 'poles' as const, label: 'Pôles', icon: Building2, count: poles?.length || 0 },
            { id: 'parcours' as const, label: 'Parcours', icon: GitBranch, count: parcoursList?.length || 0 },
            { id: 'options' as const, label: 'Options', icon: Layers, count: options?.length || 0 },
            { id: 'classes' as const, label: 'Classes', icon: Copy, count: classesParNiveau?.reduce((sum, n) => sum + n.total, 0) || 0 },
          ].map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center space-x-2 py-4 px-1 border-b-2 font-medium text-sm transition ${
                activeTab === tab.id
                  ? 'border-blue-500 text-blue-600 dark:text-blue-400'
                  : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 hover:border-gray-300 dark:hover:border-gray-600'
              }`}
            >
              <tab.icon size={18} />
              <span>{tab.label}</span>
              {tab.count > 0 && (
                <span className={`ml-1 px-1.5 py-0.5 text-xs rounded-full ${
                  activeTab === tab.id
                    ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300'
                    : 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-400'
                }`}>
                  {tab.count}
                </span>
              )}
            </button>
          ))}
        </nav>
      </div>

      {/* ========== ONGLET PÔLES ========== */}
      {activeTab === 'poles' && (() => {
        const polesFiltres = (poles || []).filter(p => {
          if (!searchPoles.trim()) return true;
          const q = searchPoles.toLowerCase();
          return p.nom.toLowerCase().includes(q) || (p.code || '').toLowerCase().includes(q);
        });
        return (
          <Card className="dark:bg-gray-800 dark:border-gray-700">
            {/* Toolbar */}
            <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 p-4 border-b border-gray-200 dark:border-gray-700">
              <div className="relative flex-1 min-w-0 max-w-md">
                <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                <input
                  type="text"
                  value={searchPoles}
                  onChange={(e) => setSearchPoles(e.target.value)}
                  placeholder="Rechercher un pôle…"
                  className="w-full pl-9 pr-3 py-2 text-sm bg-white dark:bg-gray-900 border border-gray-300 dark:border-gray-600 rounded text-gray-900 dark:text-gray-100 focus:ring-1 focus:ring-blue-500 focus:border-blue-500 outline-none"
                />
              </div>
              <div className="text-xs text-gray-500 dark:text-gray-400 whitespace-nowrap">
                {polesFiltres.length} / {poles?.length || 0}
              </div>
              <Button variant="primary" size="sm" icon={<Plus size={14} />} onClick={() => { resetPoleForm(); setShowPoleForm(true); }}>
                Nouveau pôle
              </Button>
            </div>

            {/* Table */}
            {polesFiltres.length > 0 ? (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50 dark:bg-gray-700/50 text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400">
                    <tr>
                      <th className="px-4 py-2.5 text-left font-semibold">Code</th>
                      <th className="px-4 py-2.5 text-left font-semibold">Nom</th>
                      <th className="px-4 py-2.5 text-left font-semibold hidden md:table-cell">Description</th>
                      <th className="px-4 py-2.5 text-center font-semibold">Parcours</th>
                      <th className="px-4 py-2.5 text-center font-semibold">Options</th>
                      <th className="px-4 py-2.5 text-center font-semibold">Statut</th>
                      <th className="px-4 py-2.5 text-right font-semibold">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                    {polesFiltres.map(pole => (
                      <tr key={pole.id} className="hover:bg-gray-50 dark:hover:bg-gray-700/40 transition-colors">
                        <td className="px-4 py-2.5 font-mono text-xs text-gray-500 dark:text-gray-400">{pole.code || '—'}</td>
                        <td className="px-4 py-2.5 font-medium text-gray-900 dark:text-gray-100">{pole.nom}</td>
                        <td className="px-4 py-2.5 text-gray-500 dark:text-gray-400 hidden md:table-cell max-w-xs truncate">
                          {(pole as any).description || <span className="italic text-gray-400">—</span>}
                        </td>
                        <td className="px-4 py-2.5 text-center text-gray-700 dark:text-gray-300 tabular-nums">{getParcoursByPole(pole.id).length}</td>
                        <td className="px-4 py-2.5 text-center text-gray-700 dark:text-gray-300 tabular-nums">{getOptionsByPole(pole.id).length}</td>
                        <td className="px-4 py-2.5 text-center">
                          <span className={`inline-flex items-center gap-1 text-xs font-medium ${pole.actif ? 'text-green-700 dark:text-green-400' : 'text-gray-400'}`}>
                            <Circle size={7} fill="currentColor" strokeWidth={0} />
                            {pole.actif ? 'Actif' : 'Inactif'}
                          </span>
                        </td>
                        <td className="px-4 py-2.5">
                          <div className="flex items-center justify-end gap-1">
                            <button onClick={() => openEditPole(pole)} title="Modifier" className="p-1.5 rounded text-gray-500 hover:text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900/30 transition-colors">
                              <Edit size={14} />
                            </button>
                            <button onClick={() => askDeletePole(pole)} title="Supprimer" className="p-1.5 rounded text-gray-500 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/30 transition-colors">
                              <Trash2 size={14} />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="p-12 text-center">
                <Building2 className="w-10 h-10 text-gray-300 dark:text-gray-600 mx-auto mb-3" />
                {searchPoles ? (
                  <p className="text-sm text-gray-500 dark:text-gray-400">Aucun pôle ne correspond à « {searchPoles} »</p>
                ) : (
                  <>
                    <p className="text-sm text-gray-500 dark:text-gray-400 mb-3">Aucun pôle configuré</p>
                    <Button variant="primary" size="sm" icon={<Plus size={14} />} onClick={() => setShowPoleForm(true)}>
                      Créer le premier pôle
                    </Button>
                  </>
                )}
              </div>
            )}
          </Card>
        );
      })()}

      {/* ========== ONGLET PARCOURS ========== */}
      {activeTab === 'parcours' && (() => {
        const polesById = new Map((poles || []).map(p => [p.id, p]));
        const parcoursFiltres = (parcoursList || []).filter(p => {
          if (filterParcoursPole && p.pole !== filterParcoursPole) return false;
          if (searchParcours.trim()) {
            const q = searchParcours.toLowerCase();
            if (!p.nom.toLowerCase().includes(q) && !(p.code || '').toLowerCase().includes(q)) return false;
          }
          return true;
        });
        return (
          <Card className="dark:bg-gray-800 dark:border-gray-700">
            {/* Toolbar */}
            <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 p-4 border-b border-gray-200 dark:border-gray-700">
              <div className="relative flex-1 min-w-0 max-w-md">
                <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                <input
                  type="text"
                  value={searchParcours}
                  onChange={(e) => setSearchParcours(e.target.value)}
                  placeholder="Rechercher un parcours…"
                  className="w-full pl-9 pr-3 py-2 text-sm bg-white dark:bg-gray-900 border border-gray-300 dark:border-gray-600 rounded text-gray-900 dark:text-gray-100 focus:ring-1 focus:ring-blue-500 focus:border-blue-500 outline-none"
                />
              </div>
              <select
                value={filterParcoursPole}
                onChange={(e) => setFilterParcoursPole(e.target.value ? Number(e.target.value) : '')}
                className="px-3 py-2 text-sm bg-white dark:bg-gray-900 border border-gray-300 dark:border-gray-600 rounded text-gray-900 dark:text-gray-100"
              >
                <option value="">Tous les pôles</option>
                {poles?.map(p => <option key={p.id} value={p.id}>{p.nom}</option>)}
              </select>
              <div className="text-xs text-gray-500 dark:text-gray-400 whitespace-nowrap">
                {parcoursFiltres.length} / {parcoursList?.length || 0}
              </div>
              <Button variant="primary" size="sm" icon={<Plus size={14} />} onClick={() => { resetParcoursForm(); setShowParcoursForm(true); }}>
                Nouveau parcours
              </Button>
            </div>

            {isLoadingParcours ? (
              <div className="flex justify-center py-12"><Spinner size="lg" /></div>
            ) : parcoursFiltres.length > 0 ? (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50 dark:bg-gray-700/50 text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400">
                    <tr>
                      <th className="px-4 py-2.5 text-left font-semibold">Code</th>
                      <th className="px-4 py-2.5 text-left font-semibold">Nom</th>
                      <th className="px-4 py-2.5 text-left font-semibold">Pôle</th>
                      <th className="px-4 py-2.5 text-center font-semibold">Options</th>
                      <th className="px-4 py-2.5 text-center font-semibold">Statut</th>
                      <th className="px-4 py-2.5 text-right font-semibold">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                    {parcoursFiltres.map(p => {
                      const nbOptions = getOptionsByParcours(p.id).length;
                      return (
                        <tr key={p.id} className="hover:bg-gray-50 dark:hover:bg-gray-700/40 transition-colors">
                          <td className="px-4 py-2.5 font-mono text-xs text-gray-500 dark:text-gray-400">{p.code || '—'}</td>
                          <td className="px-4 py-2.5 font-medium text-gray-900 dark:text-gray-100">{p.nom}</td>
                          <td className="px-4 py-2.5 text-gray-700 dark:text-gray-300">{polesById.get(p.pole)?.nom || '—'}</td>
                          <td className="px-4 py-2.5 text-center text-gray-700 dark:text-gray-300 tabular-nums">{nbOptions}</td>
                          <td className="px-4 py-2.5 text-center">
                            <span className={`inline-flex items-center gap-1 text-xs font-medium ${p.actif ? 'text-green-700 dark:text-green-400' : 'text-gray-400'}`}>
                              <Circle size={7} fill="currentColor" strokeWidth={0} />
                              {p.actif ? 'Actif' : 'Inactif'}
                            </span>
                          </td>
                          <td className="px-4 py-2.5">
                            <div className="flex items-center justify-end gap-1">
                              <button onClick={() => openEditParcours(p)} title="Modifier" className="p-1.5 rounded text-gray-500 hover:text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900/30 transition-colors">
                                <Edit size={14} />
                              </button>
                              <button onClick={() => askDeleteParcours(p)} title="Supprimer" className="p-1.5 rounded text-gray-500 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/30 transition-colors">
                                <Trash2 size={14} />
                              </button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="p-12 text-center">
                <GitBranch className="w-10 h-10 text-gray-300 dark:text-gray-600 mx-auto mb-3" />
                {(searchParcours || filterParcoursPole) ? (
                  <p className="text-sm text-gray-500 dark:text-gray-400">Aucun parcours ne correspond aux critères</p>
                ) : (
                  <>
                    <p className="text-sm text-gray-500 dark:text-gray-400 mb-3">Aucun parcours configuré</p>
                    <Button variant="primary" size="sm" icon={<Plus size={14} />} onClick={() => setShowParcoursForm(true)}>
                      Créer le premier parcours
                    </Button>
                  </>
                )}
              </div>
            )}
          </Card>
        );
      })()}

      {/* ========== ONGLET OPTIONS (v2 refonte) ========== */}
      {activeTab === 'options' && (() => {
        const polesById = new Map((poles || []).map(p => [p.id, p]));
        const parcoursById = new Map((parcoursList || []).map(p => [p.id, p]));
        const optionsFiltrees = (options || []).filter(o => {
          if (filterOptionsPole && o.pole !== filterOptionsPole) return false;
          if (filterOptionsParcours) {
            if (o.parcours !== filterOptionsParcours) return false;
          }
          if (searchOptions.trim()) {
            const q = searchOptions.toLowerCase();
            if (!o.nom.toLowerCase().includes(q) && !(o.code || '').toLowerCase().includes(q)) return false;
          }
          return true;
        });
        // Index classes par option pour afficher les niveaux
        const niveauxByOption: Record<number, string[]> = {};
        classesParNiveau?.forEach(niv => niv.classes.forEach(cl => {
          const opt = (options || []).find(o => o.nom === cl.option_nom);
          if (opt) {
            if (!niveauxByOption[opt.id]) niveauxByOption[opt.id] = [];
            if (!niveauxByOption[opt.id].includes(niv.niveau)) niveauxByOption[opt.id].push(niv.niveau);
          }
        }));
        // Parcours disponibles selon le pôle filtré
        const parcoursDispo = filterOptionsPole
          ? (parcoursList || []).filter(p => p.pole === filterOptionsPole)
          : (parcoursList || []);
        return (
          <Card className="dark:bg-gray-800 dark:border-gray-700">
            {/* Toolbar */}
            <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 p-4 border-b border-gray-200 dark:border-gray-700 flex-wrap">
              <div className="relative flex-1 min-w-0 max-w-md">
                <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                <input
                  type="text"
                  value={searchOptions}
                  onChange={(e) => setSearchOptions(e.target.value)}
                  placeholder="Rechercher une option…"
                  className="w-full pl-9 pr-3 py-2 text-sm bg-white dark:bg-gray-900 border border-gray-300 dark:border-gray-600 rounded text-gray-900 dark:text-gray-100 focus:ring-1 focus:ring-blue-500 focus:border-blue-500 outline-none"
                />
              </div>
              <select
                value={filterOptionsPole}
                onChange={(e) => { setFilterOptionsPole(e.target.value ? Number(e.target.value) : ''); setFilterOptionsParcours(''); }}
                className="px-3 py-2 text-sm bg-white dark:bg-gray-900 border border-gray-300 dark:border-gray-600 rounded text-gray-900 dark:text-gray-100"
              >
                <option value="">Tous les pôles</option>
                {poles?.map(p => <option key={p.id} value={p.id}>{p.nom}</option>)}
              </select>
              <select
                value={filterOptionsParcours}
                onChange={(e) => setFilterOptionsParcours(e.target.value ? Number(e.target.value) : '')}
                className="px-3 py-2 text-sm bg-white dark:bg-gray-900 border border-gray-300 dark:border-gray-600 rounded text-gray-900 dark:text-gray-100"
              >
                <option value="">Tous les parcours</option>
                {parcoursDispo.map(p => <option key={p.id} value={p.id}>{p.nom}</option>)}
              </select>
              <div className="text-xs text-gray-500 dark:text-gray-400 whitespace-nowrap">
                {optionsFiltrees.length} / {options?.length || 0}
              </div>
              <Button variant="outline" size="sm" icon={<Copy size={14} />} onClick={() => setShowAutoCSModal(true)} title="Auto-créer les options CS depuis les étudiants type=CS">
                Auto-créer CS
              </Button>
              <Button variant="primary" size="sm" icon={<Plus size={14} />} onClick={() => { resetOptionForm(); setShowOptionForm(true); }}>
                Nouvelle option
              </Button>
            </div>

            {optionsFiltrees.length > 0 ? (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50 dark:bg-gray-700/50 text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400">
                    <tr>
                      <th className="px-4 py-2.5 text-left font-semibold">Code</th>
                      <th className="px-4 py-2.5 text-left font-semibold">Nom</th>
                      <th className="px-4 py-2.5 text-left font-semibold">Pôle</th>
                      <th className="px-4 py-2.5 text-left font-semibold">Parcours</th>
                      <th className="px-4 py-2.5 text-left font-semibold">Niveaux</th>
                      <th className="px-4 py-2.5 text-center font-semibold">Statut</th>
                      <th className="px-4 py-2.5 text-right font-semibold">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                    {optionsFiltrees.map(option => (
                      <tr key={option.id} className="hover:bg-gray-50 dark:hover:bg-gray-700/40 transition-colors">
                        <td className="px-4 py-2.5 font-mono text-xs text-gray-500 dark:text-gray-400">{option.code || '—'}</td>
                        <td className="px-4 py-2.5 font-medium text-gray-900 dark:text-gray-100">{option.nom}</td>
                        <td className="px-4 py-2.5 text-gray-700 dark:text-gray-300">{polesById.get(option.pole)?.nom || '—'}</td>
                        <td className="px-4 py-2.5 text-gray-700 dark:text-gray-300">
                          {option.parcours ? parcoursById.get(option.parcours)?.nom : <span className="italic text-gray-400">—</span>}
                        </td>
                        <td className="px-4 py-2.5">
                          <div className="flex flex-wrap gap-1">
                            {(niveauxByOption[option.id] || []).sort().map(niv => (
                              <span key={niv} className="px-1.5 py-0.5 bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 text-xs rounded font-mono">
                                {niv}
                              </span>
                            ))}
                            {(!niveauxByOption[option.id] || niveauxByOption[option.id].length === 0) && (
                              <span className="text-xs text-gray-400 italic">Pas de classe</span>
                            )}
                          </div>
                        </td>
                        <td className="px-4 py-2.5 text-center">
                          <div className="flex flex-col items-center gap-1">
                            <span className={`inline-flex items-center gap-1 text-xs font-medium ${option.actif ? 'text-green-700 dark:text-green-400' : 'text-gray-400'}`}>
                              <Circle size={7} fill="currentColor" strokeWidth={0} />
                              {option.actif ? 'Actif' : 'Inactif'}
                            </span>
                            {option.exclue_edt_examens && (
                              <span className="inline-flex items-center gap-1 text-[10px] font-medium text-amber-700 dark:text-amber-400"
                                title="Cette option est masquée lors de la génération de l'EDT examens">
                                <EyeOff size={10} /> Masquée EDT
                              </span>
                            )}
                          </div>
                        </td>
                        <td className="px-4 py-2.5">
                          <div className="flex items-center justify-end gap-1">
                            <button
                              onClick={async () => {
                                try {
                                  await updateOption({
                                    id: option.id,
                                    data: { exclue_edt_examens: !option.exclue_edt_examens },
                                  }).unwrap();
                                  refetchOptions();
                                } catch (err: any) {
                                  alert(err?.data?.detail || 'Erreur');
                                }
                              }}
                              title={option.exclue_edt_examens
                                ? "Réintégrer dans la génération EDT examens"
                                : "Masquer cette option dans la génération EDT examens"}
                              className={`p-1.5 rounded transition-colors ${
                                option.exclue_edt_examens
                                  ? 'text-amber-600 hover:bg-amber-50 dark:hover:bg-amber-900/30'
                                  : 'text-gray-500 hover:text-amber-600 hover:bg-amber-50 dark:hover:bg-amber-900/30'
                              }`}
                            >
                              {option.exclue_edt_examens ? <EyeOff size={14} /> : <Eye size={14} />}
                            </button>
                            <button
                              onClick={() => handleDupliquerCS(option)}
                              disabled={duplicatingCsId === option.id || option.code?.endsWith('-CS')}
                              title="Dupliquer pour Cours du Soir (CS)"
                              className="p-1.5 rounded text-gray-500 hover:text-purple-600 hover:bg-purple-50 dark:hover:bg-purple-900/30 transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
                            >
                              <Copy size={14} />
                            </button>
                            <button onClick={() => openEditOption(option)} title="Modifier" className="p-1.5 rounded text-gray-500 hover:text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900/30 transition-colors">
                              <Edit size={14} />
                            </button>
                            <button onClick={() => askDeleteOption(option)} title="Supprimer" className="p-1.5 rounded text-gray-500 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/30 transition-colors">
                              <Trash2 size={14} />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="p-12 text-center">
                <Layers className="w-10 h-10 text-gray-300 dark:text-gray-600 mx-auto mb-3" />
                {(searchOptions || filterOptionsPole || filterOptionsParcours) ? (
                  <p className="text-sm text-gray-500 dark:text-gray-400">Aucune option ne correspond aux critères</p>
                ) : (
                  <>
                    <p className="text-sm text-gray-500 dark:text-gray-400 mb-3">Aucune option configurée</p>
                    <Button variant="primary" size="sm" icon={<Plus size={14} />} onClick={() => setShowOptionForm(true)}>
                      Créer la première option
                    </Button>
                  </>
                )}
              </div>
            )}
          </Card>
        );
      })()}


      {/* ========== ONGLET CLASSES (v2 refonte) ========== */}
      {activeTab === 'classes' && (() => {
        const polesById = new Map((poles || []).map(p => [p.id, p]));
        const optionsById = new Map((options || []).map(o => [o.id, o]));
        // Aplatir toutes les classes
        const allClasses: ClasseList[] = [];
        classesParNiveau?.forEach(niv => niv.classes.forEach(cl => allClasses.push(cl)));
        // Filtres
        const classesFiltrees = allClasses.filter(cl => {
          if (filterClassesNiveau && cl.niveau !== filterClassesNiveau) return false;
          if (filterClassesPole) {
            const opt = optionsById.get(cl.option_id);
            if (!opt || opt.pole !== filterClassesPole) return false;
          }
          if (searchClasses.trim()) {
            const q = searchClasses.toLowerCase();
            if (!cl.code.toLowerCase().includes(q) && !(cl.option_nom || '').toLowerCase().includes(q)) return false;
          }
          return true;
        });
        // Tri : niveau puis code classe
        classesFiltrees.sort((a, b) => {
          if (a.niveau !== b.niveau) return a.niveau.localeCompare(b.niveau);
          return a.code.localeCompare(b.code);
        });
        return (
          <Card className="dark:bg-gray-800 dark:border-gray-700">
            {/* Toolbar */}
            <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 p-4 border-b border-gray-200 dark:border-gray-700 flex-wrap">
              <div className="relative flex-1 min-w-0 max-w-md">
                <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                <input
                  type="text"
                  value={searchClasses}
                  onChange={(e) => setSearchClasses(e.target.value)}
                  placeholder="Rechercher une classe…"
                  className="w-full pl-9 pr-3 py-2 text-sm bg-white dark:bg-gray-900 border border-gray-300 dark:border-gray-600 rounded text-gray-900 dark:text-gray-100 focus:ring-1 focus:ring-blue-500 focus:border-blue-500 outline-none"
                />
              </div>
              <select
                value={filterClassesNiveau}
                onChange={(e) => setFilterClassesNiveau(e.target.value)}
                className="px-3 py-2 text-sm bg-white dark:bg-gray-900 border border-gray-300 dark:border-gray-600 rounded text-gray-900 dark:text-gray-100"
              >
                <option value="">Tous les niveaux</option>
                {NIVEAUX.map(n => <option key={n.value} value={n.value}>{n.label}</option>)}
              </select>
              <select
                value={filterClassesPole}
                onChange={(e) => setFilterClassesPole(e.target.value ? Number(e.target.value) : '')}
                className="px-3 py-2 text-sm bg-white dark:bg-gray-900 border border-gray-300 dark:border-gray-600 rounded text-gray-900 dark:text-gray-100"
              >
                <option value="">Tous les pôles</option>
                {poles?.map(p => <option key={p.id} value={p.id}>{p.nom}</option>)}
              </select>
              <div className="text-xs text-gray-500 dark:text-gray-400 whitespace-nowrap">
                {classesFiltrees.length} / {allClasses.length}
              </div>
              <Button variant="outline" size="sm" icon={<Copy size={14} />} onClick={() => setShowCopyForm(true)}>
                Copier niveau
              </Button>
              <Button variant="primary" size="sm" icon={<Plus size={14} />} onClick={() => { resetClasseForm(); setClasseForm({ niveau: NIVEAUX[0].value, option: options?.[0]?.id || 0 }); setShowClasseForm(true); }}>
                Nouvelle classe
              </Button>
            </div>

            {(isLoadingClasses || isLoadingOptions) ? (
              <div className="flex justify-center py-12"><Spinner size="lg" /></div>
            ) : classesFiltrees.length > 0 ? (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50 dark:bg-gray-700/50 text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400">
                    <tr>
                      <th className="px-4 py-2.5 text-left font-semibold">Code</th>
                      <th className="px-4 py-2.5 text-left font-semibold">Niveau</th>
                      <th className="px-4 py-2.5 text-left font-semibold">Option</th>
                      <th className="px-4 py-2.5 text-left font-semibold hidden md:table-cell">Pôle</th>
                      <th className="px-4 py-2.5 text-right font-semibold">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                    {classesFiltrees.map(cl => {
                      const opt = optionsById.get(cl.option_id);
                      const poleObj = opt ? polesById.get(opt.pole) : null;
                      return (
                        <tr key={cl.id} className="hover:bg-gray-50 dark:hover:bg-gray-700/40 transition-colors">
                          <td className="px-4 py-2.5 font-mono font-semibold text-gray-900 dark:text-gray-100">{cl.code}</td>
                          <td className="px-4 py-2.5">
                            <span className="inline-block px-1.5 py-0.5 bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 text-xs rounded font-mono">
                              {cl.niveau}
                            </span>
                          </td>
                          <td className="px-4 py-2.5 text-gray-700 dark:text-gray-300">{cl.option_nom || '—'}</td>
                          <td className="px-4 py-2.5 text-gray-500 dark:text-gray-400 hidden md:table-cell">{poleObj?.nom || '—'}</td>
                          <td className="px-4 py-2.5">
                            <div className="flex items-center justify-end gap-1">
                              <button onClick={() => startEditClasse(cl)} title="Modifier" className="p-1.5 rounded text-gray-500 hover:text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900/30 transition-colors">
                                <Edit size={14} />
                              </button>
                              <button onClick={() => askDeleteClasse(cl)} title="Supprimer" className="p-1.5 rounded text-gray-500 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/30 transition-colors">
                                <Trash2 size={14} />
                              </button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="p-12 text-center">
                <Copy className="w-10 h-10 text-gray-300 dark:text-gray-600 mx-auto mb-3" />
                {(searchClasses || filterClassesNiveau || filterClassesPole) ? (
                  <p className="text-sm text-gray-500 dark:text-gray-400">Aucune classe ne correspond aux critères</p>
                ) : (
                  <>
                    <p className="text-sm text-gray-500 dark:text-gray-400 mb-3">Aucune classe configurée</p>
                    <Button variant="primary" size="sm" icon={<Plus size={14} />} onClick={() => { resetClasseForm(); setClasseForm({ niveau: NIVEAUX[0].value, option: options?.[0]?.id || 0 }); setShowClasseForm(true); }}>
                      Créer la première classe
                    </Button>
                  </>
                )}
              </div>
            )}
          </Card>
        );
      })()}

      {/* ========== MODAL PÔLE ========== */}
      {showPoleForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <Card className="w-full max-w-md dark:bg-gray-800 dark:border-gray-700">
            <div className="p-6">
              <h3 className="text-lg font-bold text-gray-900 dark:text-gray-100 mb-6">
                {editingPole ? 'Modifier le Pôle' : 'Nouveau Pôle'}
              </h3>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Code</label>
                  <Input
                    value={poleForm.code}
                    onChange={(e) => setPoleForm({ ...poleForm, code: e.target.value.toUpperCase() })}
                    placeholder="Ex: POLY"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Nom</label>
                  <Input
                    value={poleForm.nom}
                    onChange={(e) => setPoleForm({ ...poleForm, nom: e.target.value })}
                    placeholder="Ex: Polytechnique"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Description (optionnel)</label>
                  <textarea
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                    rows={3}
                    value={poleForm.description || ''}
                    onChange={(e) => setPoleForm({ ...poleForm, description: e.target.value })}
                    placeholder="Description du pôle..."
                  />
                </div>
              </div>

              <div className="flex justify-end space-x-3 mt-6">
                <Button variant="outline" onClick={() => { setShowPoleForm(false); resetPoleForm(); }}>
                  Annuler
                </Button>
                <Button
                  variant="primary"
                  onClick={handleSavePole}
                  disabled={isCreatingPole || isUpdatingPole || !poleForm.code || !poleForm.nom}
                >
                  {isCreatingPole || isUpdatingPole ? 'Enregistrement...' : editingPole ? 'Modifier' : 'Créer'}
                </Button>
              </div>
            </div>
          </Card>
        </div>
      )}

      {/* ========== MODAL PARCOURS ========== */}
      {showParcoursForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <Card className="w-full max-w-md dark:bg-gray-800 dark:border-gray-700">
            <div className="p-6">
              <h3 className="text-lg font-bold text-gray-900 dark:text-gray-100 mb-6">
                {editingParcours ? 'Modifier le Parcours' : 'Nouveau Parcours'}
              </h3>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Pôle</label>
                  <select
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                    value={parcoursForm.pole}
                    onChange={(e) => setParcoursForm({ ...parcoursForm, pole: parseInt(e.target.value) })}
                  >
                    <option value={0}>Sélectionner un pôle</option>
                    {poles?.map((pole) => (
                      <option key={pole.id} value={pole.id}>{pole.nom}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Code</label>
                  <Input
                    value={parcoursForm.code}
                    onChange={(e) => setParcoursForm({ ...parcoursForm, code: e.target.value.toUpperCase() })}
                    placeholder="Ex: GM, GI, GC"
                  />
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">Code court unique dans ce pôle</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Nom</label>
                  <Input
                    value={parcoursForm.nom}
                    onChange={(e) => setParcoursForm({ ...parcoursForm, nom: e.target.value })}
                    placeholder="Ex: Génie Mécanique"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Description (optionnel)</label>
                  <textarea
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                    rows={2}
                    value={parcoursForm.description || ''}
                    onChange={(e) => setParcoursForm({ ...parcoursForm, description: e.target.value })}
                    placeholder="Description du parcours..."
                  />
                </div>
              </div>

              <div className="flex justify-end space-x-3 mt-6">
                <Button variant="outline" onClick={() => { setShowParcoursForm(false); resetParcoursForm(); }}>
                  Annuler
                </Button>
                <Button
                  variant="primary"
                  onClick={handleSaveParcours}
                  disabled={isCreatingParcours || isUpdatingParcours || !parcoursForm.code || !parcoursForm.nom || !parcoursForm.pole}
                >
                  {isCreatingParcours || isUpdatingParcours ? 'Enregistrement...' : editingParcours ? 'Modifier' : 'Créer'}
                </Button>
              </div>
            </div>
          </Card>
        </div>
      )}

      {/* ========== MODAL OPTION ========== */}
      {showOptionForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <Card className="w-full max-w-md dark:bg-gray-800 dark:border-gray-700">
            <div className="p-6">
              <h3 className="text-lg font-bold text-gray-900 dark:text-gray-100 mb-6">
                {editingOption ? 'Modifier l\'Option' : 'Nouvelle Option'}
              </h3>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Pôle</label>
                  <select
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                    value={optionForm.pole}
                    onChange={(e) => setOptionForm({ ...optionForm, pole: parseInt(e.target.value), parcours: null })}
                  >
                    <option value={0}>Sélectionner un pôle</option>
                    {poles?.map((pole) => (
                      <option key={pole.id} value={pole.id}>{pole.nom}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Parcours <span className="text-gray-400 dark:text-gray-500 font-normal">(optionnel)</span>
                  </label>
                  <select
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                    value={optionForm.parcours ?? ''}
                    onChange={(e) => setOptionForm({ ...optionForm, parcours: e.target.value ? parseInt(e.target.value) : null })}
                    disabled={!optionForm.pole}
                  >
                    <option value="">Aucun parcours</option>
                    {getParcoursForPole(optionForm.pole).map((p) => (
                      <option key={p.id} value={p.id}>{p.nom} ({p.code})</option>
                    ))}
                  </select>
                  {optionForm.pole && getParcoursForPole(optionForm.pole).length === 0 && (
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">Aucun parcours dans ce pôle. Créez-en un depuis l'onglet Parcours.</p>
                  )}
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Code</label>
                  <Input
                    value={optionForm.code}
                    onChange={(e) => setOptionForm({ ...optionForm, code: e.target.value.toUpperCase() })}
                    placeholder="Ex: MI"
                  />
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">Ce code sera utilisé pour générer le code de classe (ex: L1MI)</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Nom</label>
                  <Input
                    value={optionForm.nom}
                    onChange={(e) => setOptionForm({ ...optionForm, nom: e.target.value })}
                    placeholder="Ex: Maintenance Industrielle"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Description <span className="text-gray-400 dark:text-gray-500 font-normal">(optionnel)</span>
                  </label>
                  <textarea
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                    rows={2}
                    value={optionForm.description || ''}
                    onChange={(e) => setOptionForm({ ...optionForm, description: e.target.value })}
                    placeholder="Description de l'option..."
                  />
                </div>
              </div>

              <div className="flex justify-end space-x-3 mt-6">
                <Button variant="outline" onClick={() => { setShowOptionForm(false); resetOptionForm(); }}>
                  Annuler
                </Button>
                <Button
                  variant="primary"
                  onClick={handleSaveOption}
                  disabled={isCreatingOption || isUpdatingOption || !optionForm.code || !optionForm.nom || !optionForm.pole}
                >
                  {isCreatingOption || isUpdatingOption ? 'Enregistrement...' : editingOption ? 'Modifier' : 'Créer'}
                </Button>
              </div>
            </div>
          </Card>
        </div>
      )}

      {/* ========== MODAL CLASSE ========== */}
      {showClasseForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <Card className="w-full max-w-md dark:bg-gray-800 dark:border-gray-700">
            <div className="p-6">
              <h3 className="text-lg font-bold text-gray-900 dark:text-gray-100 mb-6">
                {editingClasse ? 'Modifier la Classe' : 'Nouvelle Classe'}
              </h3>

              <div className="space-y-4">
                {editingClasse && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Code de la classe</label>
                    <Input
                      value={classeCode}
                      onChange={(e) => setClasseCode(e.target.value.toUpperCase())}
                      placeholder="Ex: L1MI, L2GRH"
                    />
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">Code affiché pour cette classe (doit être unique)</p>
                  </div>
                )}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Niveau</label>
                  <select
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                    value={classeForm.niveau}
                    onChange={(e) => setClasseForm({ ...classeForm, niveau: e.target.value })}
                  >
                    <option value="">Sélectionner un niveau</option>
                    {NIVEAUX.map((n) => (
                      <option key={n.value} value={n.value}>{n.label}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Option</label>
                  <select
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                    value={classeForm.option}
                    onChange={(e) => setClasseForm({ ...classeForm, option: parseInt(e.target.value) })}
                  >
                    <option value={0}>Sélectionner une option</option>
                    {options?.map((opt) => (
                      <option key={opt.id} value={opt.id}>
                        {opt.nom} ({opt.code}) — {opt.pole_nom}
                        {opt.parcours_nom ? ` / ${opt.parcours_nom}` : ''}
                      </option>
                    ))}
                  </select>
                </div>

                {!editingClasse && classeForm.niveau && classeForm.option > 0 && (
                  <div className="p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
                    <p className="text-sm text-blue-800 dark:text-blue-300">
                      Code généré: <strong>{classeForm.niveau}{options?.find(o => o.id === classeForm.option)?.code}</strong>
                    </p>
                  </div>
                )}
              </div>

              <div className="flex justify-end space-x-3 mt-6">
                <Button variant="outline" onClick={() => { setShowClasseForm(false); resetClasseForm(); }}>
                  Annuler
                </Button>
                <Button
                  variant="primary"
                  onClick={handleSaveClasse}
                  disabled={isCreatingClasse || isUpdatingClasse || !classeForm.niveau || !classeForm.option}
                >
                  {isCreatingClasse || isUpdatingClasse ? 'Enregistrement...' : editingClasse ? 'Modifier' : 'Créer'}
                </Button>
              </div>
            </div>
          </Card>
        </div>
      )}

      {/* ========== MODAL COPIE NIVEAU ========== */}
      {showCopyForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <Card className="w-full max-w-md dark:bg-gray-800 dark:border-gray-700">
            <div className="p-6">
              <h3 className="text-lg font-bold text-gray-900 dark:text-gray-100 mb-2">Copier les Classes</h3>
              <p className="text-gray-600 dark:text-gray-400 text-sm mb-6">
                Copiez toutes les classes d'un niveau vers un autre. Le préfixe du niveau sera automatiquement ajusté (L1MI → L2MI).
              </p>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Niveau source</label>
                  <select
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                    value={copyForm.niveau_source}
                    onChange={(e) => setCopyForm({ ...copyForm, niveau_source: e.target.value })}
                  >
                    <option value="">Sélectionner</option>
                    {NIVEAUX.map((n) => (
                      <option key={n.value} value={n.value}>{n.label}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Niveau destination</label>
                  <select
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                    value={copyForm.niveau_destination}
                    onChange={(e) => setCopyForm({ ...copyForm, niveau_destination: e.target.value })}
                  >
                    <option value="">Sélectionner</option>
                    {NIVEAUX.filter(n => n.value !== copyForm.niveau_source).map((n) => (
                      <option key={n.value} value={n.value}>{n.label}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="flex justify-end space-x-3 mt-6">
                <Button variant="outline" onClick={() => setShowCopyForm(false)}>
                  Annuler
                </Button>
                <Button
                  variant="primary"
                  onClick={handleCopierNiveau}
                  disabled={isCopying || !copyForm.niveau_source || !copyForm.niveau_destination}
                  icon={<Copy size={16} />}
                >
                  {isCopying ? 'Copie...' : 'Copier'}
                </Button>
              </div>
            </div>
          </Card>
        </div>
      )}

      {/* ========== MODAL CONFIRMATION SUPPRESSION ========== */}
      {deleteConfirm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <Card className="w-full max-w-md dark:bg-gray-800 dark:border-gray-700">
            <div className="p-6">
              <div className="flex items-center space-x-3 mb-4">
                <div className="w-10 h-10 bg-red-100 dark:bg-red-900/30 rounded-full flex items-center justify-center flex-shrink-0">
                  <Trash2 className="text-red-600 dark:text-red-400" size={20} />
                </div>
                <h3 className="text-lg font-bold text-gray-900 dark:text-gray-100">Confirmer la suppression</h3>
              </div>

              <p className="text-gray-700 dark:text-gray-300 mb-2">
                Voulez-vous vraiment supprimer <strong>{deleteConfirm.label}</strong> ?
              </p>
              <p className="text-sm text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-900/20 rounded-lg p-3 border border-red-200 dark:border-red-800">
                <AlertCircle size={14} className="inline mr-1 -mt-0.5" />
                {deleteConfirm.warning}
              </p>

              <div className="flex justify-end space-x-3 mt-6">
                <Button variant="outline" onClick={() => setDeleteConfirm(null)}>
                  Annuler
                </Button>
                <Button variant="danger" icon={<Trash2 size={16} />} onClick={handleConfirmDelete}>
                  Supprimer
                </Button>
              </div>
            </div>
          </Card>
        </div>
      )}

      {/* Info */}
      <Card className="bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-700">
        <div className="flex items-start">
          <Building2 className="text-blue-600 dark:text-blue-400 mr-3 mt-1" size={20} />
          <div>
            <h4 className="font-semibold text-blue-900 dark:text-blue-300 mb-2">Organisation de la structure</h4>
            <ul className="text-sm text-blue-800 dark:text-blue-400 space-y-1">
              <li>• <strong>Pôles</strong> : Polytechnique, Commerce, Juridique — regroupent les parcours par domaine</li>
              <li>• <strong>Parcours</strong> : Ex. Génie Mécanique — regroupe les options ayant le même programme en L1</li>
              <li>• <strong>Options</strong> : Ex. Maintenance Industrielle (MI) — chaque option appartient à un pôle et optionnellement à un parcours</li>
              <li>• <strong>Classes</strong> : Combinaison niveau + option (ex: L1MI = Licence 1 Maintenance Industrielle)</li>
            </ul>
          </div>
        </div>
      </Card>

      {/* Modal duplication pour Formation Continue */}
      <DupliquerPourCSModal
        open={optionCsModal !== null}
        onClose={() => setOptionCsModal(null)}
        option={optionCsModal ? { id: optionCsModal.id, code: optionCsModal.code, nom: optionCsModal.nom } : null}
        onDone={(msg) => showNotification('success', msg)}
      />

      {/* Modal auto-création options CS en lot */}
      <AutoCreerOptionsCSModal
        open={showAutoCSModal}
        onClose={() => setShowAutoCSModal(false)}
        onDone={(msg) => showNotification('success', msg)}
      />
    </div>
  );
};

export default GestionOptions;
